const RELOAD_FLAG = "digiform:chunk-reload-attempt";

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

const matchesChunkMessage = (message: unknown): boolean => {
  if (typeof message !== "string") return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

export const isChunkError = (error: unknown): boolean => {
  if (!error) return false;
  if (typeof error === "string") return matchesChunkMessage(error);
  if (error instanceof Error) return matchesChunkMessage(error.message);
  if (typeof error === "object" && "message" in error) {
    return matchesChunkMessage((error as { message: unknown }).message);
  }
  return false;
};

// Snapshot taken once at module load. True iff a previous page load already
// triggered an automatic reload — meaning the current load is the result of
// that reload. In that state we must NOT reload again (avoid an infinite
// loop) and let callers surface the error normally.
const reloadAlreadyAttempted =
  typeof sessionStorage !== "undefined" &&
  sessionStorage.getItem(RELOAD_FLAG) === "1";

// In-memory: true once we have kicked off `window.location.reload()` in this
// page load. Used to swallow the burst of concurrent rejections that follow
// a deploy (e.g. multiple `await import(...)` in `Promise.all`) without
// firing N notifications and without re-triggering the reload N times.
let reloadInFlight = false;

/**
 * Attempts a one-shot reload on a chunk error. Returns true when the caller
 * should swallow the error (a reload was just kicked off, or one is already
 * in flight). Returns false when a reload was already attempted in a prior
 * page load — in that case the caller should fall back to surfacing the
 * error to the user, since reloading again would just loop.
 */
export const reloadForChunkErrorOnce = (): boolean => {
  if (reloadAlreadyAttempted) return false;
  if (reloadInFlight) return true;
  reloadInFlight = true;
  sessionStorage.setItem(RELOAD_FLAG, "1");
  window.location.reload();
  return true;
};

export const clearChunkReloadFlag = () => {
  sessionStorage.removeItem(RELOAD_FLAG);
};
